import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractComponent, Card } from 'lib-trend-core';

export interface PresentationDateDialogData {
  card: Card;
}

/**
 * What the dialog closes with. `null` is a cancel — the caller reverts the move;
 * `skipped` moves the deal without a date; `presentedAt` moves it with one.
 */
export type PresentationDateDialogResult =
  | { presentedAt: string }
  | { skipped: true }
  | null;

/**
 * Offers the date and time of the presentation meeting when a deal enters a
 * PRESENTATION stage without one (E2 RF-15 · plan AD-08, amended 2026-09-16). The
 * value travels WITH the move when given, and the move goes through without it: the
 * date is not required — the meeting may not be booked yet, and E4 will fill it from
 * a held meeting later. Only "Cancel" leaves the deal where it was.
 */
@Component({
  selector: 'app-presentation-date-dialog',
  templateUrl: './presentation-date-dialog.component.html',
  styleUrls: ['./presentation-date-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PresentationDateDialogComponent extends AbstractComponent implements OnInit {

  form!: FormGroup;

  constructor(
    injector: Injector,
    private readonly dialogRef: MatDialogRef<PresentationDateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PresentationDateDialogData,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    const now = new Date();
    this.form = this.formBuilder.group({
      date: [now, Validators.required],
      time: [PresentationDateDialogComponent.timeOf(now), Validators.required],
    });
  }

  confirm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { date, time } = this.form.value;
    const result: PresentationDateDialogResult = { presentedAt: PresentationDateDialogComponent.combine(date, time).toISOString() };
    this.dialogRef.close(result);
  }

  /** The move goes ahead and the date stays empty — recorded later, by hand or by E4. */
  skip(): void {
    const result: PresentationDateDialogResult = { skipped: true };
    this.dialogRef.close(result);
  }

  close(): void {
    this.dialogRef.close(null);
  }

  /** Joins the picked day with the typed `HH:mm` — the datepicker only knows the day. */
  static combine(date: Date | string, time: string): Date {
    const [hours, minutes] = `${time ?? ''}`.split(':').map(Number);
    const combined = new Date(date);
    combined.setHours(hours || 0, minutes || 0, 0, 0);
    return combined;
  }

  static timeOf(date: Date): string {
    return `${`${date.getHours()}`.padStart(2, '0')}:${`${date.getMinutes()}`.padStart(2, '0')}`;
  }
}
